import React, { useState, useEffect, useRef, useCallback } from "react";
import ProfilePage from "../UserProfile/Profile";
import CreatePrompts from "../Professions/CreatePrompts";
import PricingPage from "../Billing/PricingPage";
import { Stepper, Step, StepLabel, Box, Paper, Typography, Button, CircularProgress } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SETUP_COMPLETE_LOCAL_KEY } from "../../config";

// --- Step Definitions with Slugs ---
const steps = [
    { label: 'Profile Settings', slug: 'profile', path: '/update-profile' },
    { label: 'Select Plan', slug: 'plan', path: '/select-plan' },
];
const defaultStepSlug = steps[0].slug;

// Helper to find step index from SLUG
const getStepIndexFromSlug = (slug) => {
    const index = steps.findIndex(step => step.slug === slug);
    return index === -1 ? 0 : index; // Default to 0 if slug invalid
};

// Helper to find slug from PATH (to map backend response)
const getSlugFromPath = (pathname) => {
    const step = steps.find(s => s.path === pathname);
    return step ? step.slug : null;
};

// Helper to determine the next *required* step SLUG based on localStorage data
// **** ENSURE `userData.llm_settings` is the correct key for prompt data ****
const determineRequiredStepSlug = (userData) => {
    if (!userData) return steps[0].slug; // Should be caught by auth, but safety
    if (!userData.profile) {
        return steps[0].slug; // Need profile
    } else if (!userData.subscription) {
        return steps[1].slug; // Need plan
    } else {
        return null; // Setup complete data-wise
    }
};

// --- localStorage Key ---
let SETUP_COMPLETE_KEY = SETUP_COMPLETE_LOCAL_KEY;

// --- Helper Components ---
const InlineError = ({ message }) => message ? <Typography color="error" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>Error: {message}</Typography> : null;
const InlineSpinner = () => <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />;

// --- Styled Component ---
const StyledPaper = styled(Paper)(({ theme }) => ({ /* Styles remain the same */
    padding: theme.spacing(3), display: 'flex', flexDirection: 'column', gap: theme.spacing(2),
    margin: '50px auto', maxWidth: '60%', minWidth: '450px',
}));

// --- Main Component ---
export default function SignUpSteps() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const token = localStorage.getItem("access_token");

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessingStep, setIsProcessingStep] = useState(false);
    const [profileFormValid, setProfileFormValid] = useState(false); // Keep for button disabling
    const [createPromptsFormValid, setCreatePromptsFormValid] = useState(false); // Keep for button disabling
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0); // Initialize simply, effects will set it

    // Refs
    const profilePageRef = useRef(null);
    const createPromptsRef = useRef(null);

    // --- Effect 1: Initial Authentication & Completion Check ---
    // Runs ONCE on mount primarily.
    useEffect(() => {
        setIsLoading(true); // Start loading indicator
        setError(null);

        // 1. Auth Check
        if (!token) {
            console.log("Effect 1: Not authenticated, redirecting to Login.");
            navigate('/Login', { replace: true });
            return; // Stop here
        }

        // 2. Check if Setup is Already Complete (flag or data)
        const isSetupCompleteFlag = localStorage.getItem(SETUP_COMPLETE_KEY) === 'true';
        let userData = null;
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) userData = JSON.parse(userStr);
        } catch (e) {
            console.error("Effect 1: Error parsing user data:", e);
            setError("Failed to load user data.");
            setIsLoading(false);
            return; // Stop if data is corrupt
        }
        const requiredSlugCheck = determineRequiredStepSlug(userData); // Check data requirement

        if (isSetupCompleteFlag || requiredSlugCheck === null) {
            console.log("Effect 1: Setup complete (flag or data), redirecting to Dashboard.");
            window.location.href = "/Dashboard"; // Use window.location for final redirect
            return; // Stop here
        }

        // 3. Initial URL Check (if needed - Effect 2 will handle enforcement)
        // We can let Effect 2 handle the initial setting/enforcement of the URL param
        // to avoid potential race conditions between the effects.
        console.log("Effect 1: Auth & completion checks passed. Effect 2 will handle step enforcement.");
        setIsLoading(false); // Allow Effect 2 to run and finish loading state if needed


    // Rerun ONLY if token changes (login/logout scenario)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, navigate]);


    // --- Effect 2: Enforce Required Step & Synchronize UI ---
    // Runs whenever searchParams changes OR when the component mounts AFTER Effect 1
    useEffect(() => {
        // Don't run if initial auth check is still happening or failed
        if (!token) return;

        console.log("Effect 2: Running step enforcement and UI sync.");
        setError(null); // Clear previous errors on navigation/sync

        let userData = null;
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) userData = JSON.parse(userStr);
            // NOTE: Not setting error here, as Effect 1 should catch parsing errors on load.
            // If localStorage is modified badly *during* setup, it might cause issues.
        } catch (e) {
            console.error("Effect 2: Error parsing user data during sync:", e);
            // Optionally set an error state here if needed
        }

        // Determine the step the user *must* be on
        const requiredSlug = determineRequiredStepSlug(userData);

        // Check again for completion (in case data was updated just before this effect ran)
        if (requiredSlug === null) {
            // Check flag as well for robustness
             const isSetupCompleteFlag = localStorage.getItem(SETUP_COMPLETE_KEY) === 'true';
             if(isSetupCompleteFlag || requiredSlug === null){
                console.log("Effect 2: Data now indicates completion, redirecting to Dashboard.");
                window.location.href = "/Dashboard";
                return; // Stop this effect run
             }
        }

        // Get the step slug currently in the URL
        const currentSlugInUrl = searchParams.get('step')?.toLowerCase();

        // --- ENFORCEMENT LOGIC ---
        if (!currentSlugInUrl || getStepIndexFromSlug(currentSlugInUrl) === -1 || currentSlugInUrl !== requiredSlug) {
            // Reason 1: URL param missing
            // Reason 2: URL param is invalid slug
            // Reason 3: URL param is VALID but NOT the REQUIRED one
            if (currentSlugInUrl !== requiredSlug) { // Only log/navigate if different to avoid loops
                console.warn(`Effect 2: URL step ('${currentSlugInUrl}') is invalid or does not match required step ('${requiredSlug}'). Forcing navigation.`);
                // Use replace: true to prevent bad history entries when correcting the URL
                setSearchParams({ step: requiredSlug }, { replace: true });
                // RETURN HERE - Let the effect re-run when searchParams are updated
                return;
            }
        }

        // --- SYNC UI ---
        // If we reach here, the currentSlugInUrl IS the requiredSlug
        console.log(`Effect 2: URL step ('${currentSlugInUrl}') matches required ('${requiredSlug}'). Syncing UI.`);
        const currentStepIndex = getStepIndexFromSlug(requiredSlug); // Sync state to the validated required step
        setActiveStep(currentStepIndex);

        // Finalize states
        setIsProcessingStep(false); // Ensure button spinners stop
        setIsLoading(false); // Ensure main loading indicator stops

    // Dependencies: React to changes in URL params. Also include setSearchParams for setting them.
    }, [searchParams, setSearchParams, token]); // Add token dependency


    // --- Effect 3: Set Document Title ---
    useEffect(() => {
        document.title = 'Account Setup | KlonIT';
    }, []);


    // --- Callbacks & Handlers (Remain mostly the same) ---

    const handleStepCompletion = useCallback((nextStepUrl) => { // Expects URL like /select-plan, /Dashboard
        setIsProcessingStep(false);
        setError(null);
        console.log("HandleCompletion: Received completion URL:", nextStepUrl);

        if (nextStepUrl === "/Dashboard") {
            localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
            window.location.href = "/Dashboard";
        } else {
            const nextSlug = getSlugFromPath(nextStepUrl);
            if (nextSlug) {
                console.log(`HandleCompletion: Mapped URL ${nextStepUrl} to slug ${nextSlug}. Updating search params.`);
                // Update the URL param. Effect 2 will handle validation & UI sync.
                setSearchParams({ step: nextSlug });
            } else {
                console.warn("HandleCompletion: Received unknown next_step URL, assuming completion:", nextStepUrl);
                localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
                window.location.href = "/Dashboard";
            }
        }
    }, [setSearchParams]); // Dependency: setSearchParams

    const handleStepError = useCallback((errorMessage) => {
        setIsProcessingStep(false);
        setError(errorMessage || "An error occurred in the current step.");
    }, []);

    const handleNext = async () => {
        setError(null);
        setIsProcessingStep(true);
        const currentStepIndex = activeStep; // Use state which is synced by Effect 2

        if (currentStepIndex === 0) { // Profile
            if (profilePageRef.current?.handleSaveButtonClick) {
                 profilePageRef.current.handleSaveButtonClick();
            } else { handleStepError("Could not initiate profile save."); }
        } else if (currentStepIndex === 1) { // Prompts
            if (createPromptsRef.current?.handleGeneratePrompt) {
                 createPromptsRef.current.handleGeneratePrompt();
            } else { handleStepError("Could not initiate prompt save."); }
        } else if (currentStepIndex === 2) { // Plan
            setIsProcessingStep(false); // No action needed here
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            const previousStepSlug = steps[activeStep - 1].slug;
            // Update URL param. Effect 2 will handle validation & UI sync.
            setSearchParams({ step: previousStepSlug });
        }
    };
    const handleLogout = () => {
        localStorage.clear(); // Clear all data
        navigate("/log-out"); // Redirect to login page
    };

    // Pass validity handlers to children
    const handleProfileFormValidity = (isValid) => setProfileFormValid(isValid);
    const handleCreatePromptsFormValidity = (isValid) => setCreatePromptsFormValid(isValid);

    // Render correct child component based on the *state* (which is synced to required URL)
    const getStepContent = (stepIndex) => {
         if (stepIndex < 0 || stepIndex >= steps.length) {
              console.error(`getStepContent called with invalid index: ${stepIndex}`);
              return <Typography color="error">Invalid Step</Typography>;
         };

        switch (stepIndex) { // Use index now as it's validated
            case 0:
                return <ProfilePage
                           ref={profilePageRef}
                           onFormValidityChange={handleProfileFormValidity}
                           onComplete={handleStepCompletion}
                           onError={handleStepError}
                       />;
            case 1:
                return <PricingPage
                           onComplete={handleStepCompletion}
                           onError={handleStepError}
                           isSetupMode={true} // Pass setup mode if needed
                       />;
            default:
                return <Typography>Loading Step...</Typography>; // Should not be reached
        }
    };

    // --- Render Logic ---
    if (isLoading && !error) { // Show loading only if loading and no error
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress /> <Typography sx={{ ml: 2 }}>Checking setup status...</Typography>
            </Box>
        );
    }
     // If loading finished but there's an error (e.g., localStorage parse failed on load)
     if (error) {
         return (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                  <Typography color="error">Error during setup check: {error}</Typography>

                  <Button variant="contained" onClick={handleLogout}>
                    Logout
                  </Button>
                  {/* Optionally add a retry or logout button */}
              </Box>
          );
     }


    // Main Render once loading is complete and no critical errors
    return (
        <Box sx={{ width: '100%' }}>
            <StyledPaper elevation={3}>
                <Typography variant="h5" gutterBottom textAlign="center">Account Setup</Typography>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
                    {steps.map((step, index) => ( // Use index for key if slug isn't strictly unique or stable
                        <Step key={step.slug}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Display non-critical errors from steps */}
                <InlineError message={error} />

                <Box mt={1}>
                    {getStepContent(activeStep)}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button color="inherit" disabled={activeStep === 0 || isProcessingStep} onClick={handleBack}>
                        Back
                    </Button>
                    {activeStep < steps.length - 1 ? (
                        <Button variant="contained" onClick={handleNext}
                            // Disable button based on processing OR form validity for current step
                            disabled={
                                isProcessingStep ||
                                (activeStep === 0 && !profileFormValid)}
                            >
                           {isProcessingStep ? <InlineSpinner /> : "Save & Continue"}
                        </Button>
                    ) : (
                        <Typography variant="caption" sx={{ fontStyle: 'italic', textAlign: 'right' }}>
                            Select a plan above to complete setup.
                        </Typography>
                    )}
                </Box>
            </StyledPaper>
        </Box>
    );
}